import React, { useState } from 'react';
import { IonButton, IonIcon, IonBadge, IonText, IonSpinner } from '@ionic/react';
import { createOutline, chevronDownOutline, chevronUpOutline } from 'ionicons/icons';
import { CAItem } from '@/types/model/corrective-action';
import { apiService } from '@/services/ApiService';
import { CorrectiveActionResponseEntry } from '@/types/response/CorrectiveActionResponses';
import lookupDataService, { isValidToStatusId } from '@/services/data/LookupDataService';

interface CorrectiveActionAccordionRowProps {
  caItem: CAItem;
  index: number;
  onEdit: (caItem: CAItem) => void;
}

const GRID = '3fr 1.5fr 1fr 1.5fr 1fr 52px 52px';

const CorrectiveActionAccordionRow: React.FC<CorrectiveActionAccordionRowProps> = ({ caItem, index, onEdit }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [responses, setResponses] = useState<CorrectiveActionResponseEntry[] | null>(null);
  const [loadingResponses, setLoadingResponses] = useState(false);

  const CA_STATUSES = lookupDataService.getCorrectiveActionStatuses();
  const statusName = CA_STATUSES.find(s => s.Id === caItem.statusId)?.Name ?? `Status ${caItem.statusId}`;

  const CA_VALIDATION_STATUSES = lookupDataService.getCAValidationStatuses();
  const validationStatusId = isValidToStatusId(caItem.isValid);
  const validationStatusName = CA_VALIDATION_STATUSES.find(s => s.Id === validationStatusId)?.Name ?? '';
  const validationStatusColor = (() => {
    switch (validationStatusId) {
      case 1: return 'var(--ion-color-success)';
      case 2: return 'var(--ion-color-danger)';
      default: return 'inherit';
    }
  })();

  const statusColor = (() => {
    switch (caItem.statusId) {
      case 1: case 2: return 'warning';
      case 3: case 4: return 'success';
      case 5: return 'primary';
      case 6: return 'danger';
      default: return 'medium';
    }
  })();

  const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  const handleToggle = async () => {
    const wasExpanded = isExpanded;
    setIsExpanded(e => !e);
    if (!wasExpanded && responses === null) {
      setLoadingResponses(true);
      try {
        const data = await apiService.getCorrectiveActionResponses(caItem.id);
        setResponses(data);
      } catch {
        setResponses([]);
      } finally {
        setLoadingResponses(false);
      }
    }
  };

  const rowBg = index % 2 === 0 ? 'var(--ion-color-light-tint)' : 'white';

  return (
    <>
      {/* ── Primary row ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: GRID,
          gap: '0.75rem',
          padding: '0.75rem 1rem',
          borderBottom: '1px solid var(--ion-color-light-shade)',
          alignItems: 'center',
          background: rowBg,
        }}
      >
        <div
          style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={caItem.issue}
        >
          {caItem.issue}
        </div>

        <div style={{ fontSize: '0.85rem' }}>{fmt(caItem.issued)}</div>

        <div
          style={{ fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={caItem.issuedByName}
        >
          {caItem.issuedByName}
        </div>

        <div>
          <IonBadge color={statusColor} style={{ fontSize: '0.72rem', whiteSpace: 'normal' }}>
            {statusName}
          </IonBadge>
        </div>

        <div style={{ fontSize: '0.85rem', color: validationStatusColor, fontWeight: validationStatusId !== 0 ? 500 : 'normal' }}>
          {validationStatusName}
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <IonButton fill="clear" size="small" onClick={() => onEdit(caItem)}>
            <IonIcon icon={createOutline} />
          </IonButton>
        </div>

        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <IonButton fill="clear" size="small" onClick={handleToggle}>
            <IonIcon icon={isExpanded ? chevronUpOutline : chevronDownOutline} />
          </IonButton>
        </div>
      </div>

      {/* ── Secondary panel ── */}
      {isExpanded && (
        <div
          style={{
            background: '#f8f9fa',
            padding: '1rem 1.5rem',
            borderBottom: '2px solid var(--ion-color-light-shade)',
          }}
        >
          {/* Validation fields */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1rem',
            }}
          >
            <div>
              <IonText
                color="medium"
                style={{ fontSize: '0.72rem', fontWeight: '600', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}
              >
                VALIDATION COMMENTS
              </IonText>
              <IonText style={{ fontSize: '0.9rem' }}>{caItem.validationComments ?? '—'}</IonText>
            </div>
            <div>
              <IonText
                color="medium"
                style={{ fontSize: '0.72rem', fontWeight: '600', display: 'block', marginBottom: '4px', letterSpacing: '0.05em' }}
              >
                VALIDATED ON
              </IonText>
              <IonText style={{ fontSize: '0.9rem' }}>{fmt(caItem.validatedOn)}</IonText>
            </div>
          </div>

          {/* Responses */}
          <div>
            <IonText
              color="medium"
              style={{ fontSize: '0.72rem', fontWeight: '600', display: 'block', marginBottom: '8px', letterSpacing: '0.05em' }}
            >
              RESPONSES
            </IonText>

            {loadingResponses ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <IonSpinner name="dots" />
                <IonText color="medium" style={{ fontSize: '0.85rem' }}>Loading responses…</IonText>
              </div>
            ) : !responses || responses.length === 0 ? (
              <IonText color="medium" style={{ fontStyle: 'italic', fontSize: '0.85rem' }}>
                No responses on record.
              </IonText>
            ) : (
              <div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '120px 150px 160px 1fr',
                    gap: '8px',
                    fontSize: '0.72rem',
                    fontWeight: '600',
                    color: 'var(--ion-color-medium)',
                    borderBottom: '1px solid var(--ion-color-light-shade)',
                    paddingBottom: '4px',
                    marginBottom: '2px',
                  }}
                >
                  <div>Date</div>
                  <div>By</div>
                  <div>Status</div>
                  <div>Comments</div>
                </div>
                {responses.map(r => (
                  <div
                    key={r.Id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '120px 150px 160px 1fr',
                      gap: '8px',
                      fontSize: '0.85rem',
                      padding: '4px 0',
                      borderBottom: '1px solid var(--ion-color-light-tint)',
                    }}
                  >
                    <div>{fmt(r.Date)}</div>
                    <div>{r.Username}</div>
                    <div>{r.StatusName}</div>
                    <div>{r.Comments}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CorrectiveActionAccordionRow;
